import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Star, Gift, Ticket, User, Shield, QrCode, Users, Copy, Trash2, Download, Check } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { getConsent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Übersicht", icon: Star },
  { id: "points", label: "Meine Punkte", icon: Gift },
  { id: "coupons", label: "Meine Coupons", icon: Ticket },
  { id: "rewards", label: "Meine Prämien", icon: Gift },
  { id: "qr", label: "Mein QR-Code", icon: QrCode },
  { id: "referral", label: "Freunde werben", icon: Users },
  { id: "data", label: "Meine Daten", icon: User },
  { id: "privacy", label: "Datenschutz", icon: Shield },
];

export default function MeinKonto() {
  const { user, refresh, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [branches, setBranches] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [ref, setRef] = useState(null);
  const [profile, setProfile] = useState({});
  const [consent, setConsent] = useState(getConsent() || "");

  const load = () => api.get("/account/overview").then((r) => { setOv(r.data); setProfile(r.data.user); });
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/loyalty/rewards-catalog").then((r) => setRewards(r.data));
    api.get("/loyalty/coupons").then((r) => setCoupons(r.data));
    api.get("/account/referrals").then((r) => setRef(r.data));
  }, []);

  if (!ov) return <div className="max-w-6xl mx-auto px-4 py-24 text-gray-400">Laden…</div>;
  const u = ov.user;
  const nextReward = ov.next_reward;
  const progress = nextReward ? Math.min(100, Math.round((u.points / nextReward.points_required) * 100)) : 100;

  const redeemItem = async (item) => {
    try { const { data } = await api.post("/loyalty/redeem-item", { name: item.name, price: item.price }); toast.success(`Eingelöst! Code: ${data.code}`); load(); refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const redeemVoucher = async (v) => {
    try { const { data } = await api.post("/loyalty/redeem-voucher", { value: v.value }); toast.success(`Eingelöst! Code: ${data.code}`); load(); refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const saveProfile = async () => {
    try { await api.put("/account/profile", { first_name: profile.first_name, last_name: profile.last_name, phone: profile.phone, preferred_branch_id: profile.preferred_branch_id, birthday: profile.birthday, marketing_consent: profile.marketing_consent }); toast.success("Gespeichert"); refresh(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const exportData = async () => { const { data } = await api.get("/account/data-export"); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "helium-daten.json"; a.click(); };
  const setCookieChoice = (value) => { localStorage.setItem("helium_cookie_consent", value); window.dispatchEvent(new Event("helium-consent-change")); setConsent(value); toast.success(value === "all" ? "Analyse-Cookies aktiviert" : "Nur notwendige Cookies"); };
  const deleteAcc = async () => { if (!window.confirm("Konto wirklich löschen? Dies kann nicht rückgängig gemacht werden.")) return; await api.delete("/account/delete"); toast.success("Konto gelöscht"); logout(); };
  const copyRef = () => { navigator.clipboard.writeText(ref?.referral_code || ""); toast.success("Empfehlungscode kopiert"); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <Seo title="Mein Konto – HELIUM" />
      <h1 className="text-3xl font-extrabold font-display">Hallo, {u.first_name} 👋</h1>
      <div className="grid lg:grid-cols-4 gap-8 mt-8">
        <aside className="lg:col-span-1">
          <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`konto-tab-${t.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#E0243A] text-white" : "bg-white border border-gray-100 text-gray-700 hover:bg-gray-50"}`}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </aside>

        <div className="lg:col-span-3 space-y-6">
          {tab === "overview" && (
            <>
              <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] text-white rounded-2xl p-8 shadow-xl">
                <div className="text-gray-400 text-sm">Dein Punktestand</div>
                <div className="text-4xl font-extrabold text-[#E0243A]" data-testid="account-points">{u.points?.toLocaleString("de-DE")} Punkte</div>
                {nextReward && (
                  <div className="mt-5">
                    <div className="flex justify-between text-sm text-gray-300 mb-1"><span>Nächste Prämie: {nextReward.name}</span><span>{u.points} / {nextReward.points_required}</span></div>
                    <div className="h-2.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#E0243A]" style={{ width: `${progress}%` }} /></div>
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-6"><div className="text-sm text-gray-500">Dein Standort</div><div className="font-semibold text-lg">{ov.branch?.name || "Nicht gewählt"}</div></div>
                <div className="bg-white rounded-2xl border border-gray-100 p-6"><div className="text-sm text-gray-500">Dein persönliches Angebot</div><div className="font-semibold text-lg">{ov.offer?.title || "–"}</div></div>
              </div>
            </>
          )}

          {tab === "points" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-lg mb-4">Punkteverlauf</h2>
              <div className="divide-y divide-gray-50">
                {ov.transactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between py-3">
                    <div><div className="font-medium">{t.note}</div><div className="text-xs text-gray-400">{new Date(t.at).toLocaleDateString("de-DE")}</div></div>
                    <div className={`font-bold ${t.points >= 0 ? "text-emerald-600" : "text-[#E0243A]"}`}>{t.points >= 0 ? "+" : ""}{t.points}</div>
                  </div>
                ))}
                {ov.transactions.length === 0 && <p className="text-gray-500 py-4">Noch keine Transaktionen.</p>}
              </div>
            </div>
          )}

          {tab === "coupons" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {coupons.map((c) => (
                <div key={c.id} className={`rounded-2xl border p-5 ${c.used ? "bg-gray-50 border-gray-100 opacity-60" : "bg-white border-[#E0243A]/30"}`} data-testid="coupon-card">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-[#E0243A]">{c.code}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">{c.used ? "Verwendet" : "Aktiv"}</span>
                  </div>
                  <div className="font-semibold mt-2">{c.description}</div>
                </div>
              ))}
              {coupons.length === 0 && <p className="text-gray-500">Keine Coupons verfügbar.</p>}
            </div>
          )}

          {tab === "rewards" && (
            <div className="space-y-6">
              <div>
                <h3 className="font-bold mb-1">Menü-Prämien</h3>
                <p className="text-sm text-gray-500 mb-3">Automatisch aus dem Wochenmenü berechnet (Preis × {rewards.multiplier || 40}, gerundet auf {rewards.step || 10}). Artikel unter {(rewards.min_price ?? 1.5).toFixed(2)} € sind nicht einlösbar.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {(rewards.items || []).map((r, i) => {
                    const can = u.points >= r.points_required;
                    return (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="reward-item">
                        {r.image && <div className="h-32"><img src={r.image} alt={r.name} className="w-full h-full object-cover" /></div>}
                        <div className="p-5">
                          <div className="flex items-center justify-between"><h3 className="font-semibold">{r.name}</h3><span className="text-sm font-bold text-[#E0243A]">{r.points_required} Pkt.</span></div>
                          <p className="text-sm text-gray-500">Wert: {r.price?.toFixed(2)} €</p>
                          <button disabled={!can} onClick={() => redeemItem(r)} data-testid={`redeem-item-${i}`} className="mt-3 w-full py-2.5 rounded-xl font-semibold text-sm disabled:bg-gray-100 disabled:text-gray-400 bg-[#E0243A] text-white hover:bg-[#C81E33]">{can ? "Einlösen" : "Nicht genug Punkte"}</button>
                        </div>
                      </div>
                    );
                  })}
                  {(rewards.items || []).length === 0 && <p className="text-gray-500">Aktuell keine einlösbaren Menü-Artikel.</p>}
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-3">Gutscheine</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {(rewards.vouchers || []).map((v, i) => {
                    const can = u.points >= v.points_required;
                    return (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between" data-testid="reward-voucher">
                        <div><h3 className="font-semibold">{v.name}</h3><span className="text-sm text-gray-500">{v.points_required} Pkt.</span></div>
                        <button disabled={!can} onClick={() => redeemVoucher(v)} data-testid={`redeem-voucher-${v.value}`} className="py-2 px-4 rounded-xl font-semibold text-sm disabled:bg-gray-100 disabled:text-gray-400 bg-[#1F2937] text-white">{can ? "Einlösen" : "Zu wenig"}</button>
                      </div>
                    );
                  })}
                  {(rewards.vouchers || []).length === 0 && <p className="text-gray-500">Aktuell keine Gutscheine verfügbar.</p>}
                </div>
              </div>
            </div>
          )}

          {tab === "qr" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <h2 className="font-bold text-lg">Mein QR-Code</h2>
              <p className="text-sm text-gray-500 mt-1">Zeige diesen Code an der Kasse, um Punkte zu sammeln.</p>
              <div className="mt-6 inline-block p-6 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="customer-qr">
                <QRCodeSVG value={u.qr_token} size={220} fgColor="#1F2937" />
              </div>
              <div className="mt-4 font-mono text-sm text-gray-500">{u.qr_token}</div>
            </div>
          )}

          {tab === "referral" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <h2 className="font-bold text-lg">Freunde werben</h2>
              <p className="text-sm text-gray-500 mt-1">Teile deinen Code. Ihr erhaltet beide Bonuspunkte, sobald sich dein Freund registriert.</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex-1 bg-[#FDF2F4] text-[#C81E33] font-mono font-bold text-xl px-5 py-4 rounded-xl text-center" data-testid="referral-code">{ref?.referral_code}</div>
                <button onClick={copyRef} className="p-4 rounded-xl bg-gray-100 hover:bg-gray-200" data-testid="referral-copy"><Copy className="w-5 h-5" /></button>
              </div>
              <div className="mt-4 text-sm text-gray-600">Erfolgreiche Empfehlungen: <strong>{ref?.count || 0}</strong></div>
            </div>
          )}

          {tab === "data" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="font-bold text-lg">Meine Daten</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={profile.first_name || ""} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} placeholder="Vorname" className="px-4 py-3 rounded-xl border border-gray-200" data-testid="profile-firstname" />
                <input value={profile.last_name || ""} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} placeholder="Nachname" className="px-4 py-3 rounded-xl border border-gray-200" />
                <input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Telefon" className="px-4 py-3 rounded-xl border border-gray-200" />
                <select value={profile.preferred_branch_id || ""} onChange={(e) => setProfile({ ...profile, preferred_branch_id: e.target.value })} className="px-4 py-3 rounded-xl border border-gray-200">
                  <option value="">Standort wählen</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500 ml-1">Geburtstag (für Geburtstagsbonus 🎂)</label>
                  <input type="date" value={profile.birthday || ""} onChange={(e) => setProfile({ ...profile, birthday: e.target.value })} data-testid="profile-birthday" className="w-full px-4 py-3 rounded-xl border border-gray-200" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={!!profile.marketing_consent} onChange={(e) => setProfile({ ...profile, marketing_consent: e.target.checked })} /> Marketing-E-Mails erhalten</label>
              <button onClick={saveProfile} data-testid="profile-save" className="bg-[#E0243A] hover:bg-[#C81E33] text-white font-semibold px-6 py-3 rounded-xl">Speichern</button>
            </div>
          )}

          {tab === "privacy" && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              <h2 className="font-bold text-lg">Datenschutz & Konto</h2>
              <p className="text-sm text-gray-600">Du hast jederzeit die Kontrolle über deine Daten.</p>
              <div className="rounded-xl border border-gray-100 p-4" data-testid="consent-settings">
                <div className="font-semibold text-sm">Cookie-Einstellungen</div>
                <p className="text-sm text-gray-500 mt-1">Aktueller Status: <strong>{consent === "all" ? "Alle akzeptiert (inkl. Analyse)" : consent === "necessary" ? "Nur notwendige" : "Noch nicht gewählt"}</strong></p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => setCookieChoice("all")} data-testid="consent-accept-all" className={`px-4 py-2 rounded-xl text-sm font-semibold ${consent === "all" ? "bg-[#E0243A] text-white" : "border border-gray-200"}`}>Alle akzeptieren</button>
                  <button onClick={() => setCookieChoice("necessary")} data-testid="consent-necessary" className={`px-4 py-2 rounded-xl text-sm font-semibold ${consent === "necessary" ? "bg-[#1F2937] text-white" : "border border-gray-200"}`}>Nur notwendige</button>
                </div>
              </div>
              <button onClick={exportData} data-testid="data-export" className="flex items-center gap-2 border border-gray-200 px-5 py-3 rounded-xl font-medium hover:bg-gray-50"><Download className="w-4 h-4" /> Meine Daten exportieren</button>
              <button onClick={deleteAcc} data-testid="account-delete" className="flex items-center gap-2 text-[#E0243A] border border-[#E0243A]/30 px-5 py-3 rounded-xl font-medium hover:bg-[#FDF2F4]"><Trash2 className="w-4 h-4" /> Konto löschen</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
